import { Component, OnInit } from '@angular/core';
import { AbstractCalculatorComponentDirective } from '../abstract-calculator-component.directive';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { ConsumptionValueInputType } from '../../calculator.component';
import { PriceCountry, PriceType } from '../../../../models/price/price';
import { ParseConsumption } from '../../common/data-file-parsers/parse-consumption';
import { inOutAnimation } from '../../../../animations';
import { combineLatest } from 'rxjs';
import { ETypeCalculation } from '../../../../models/analysis/solar-calculator-request';
import { TariffModel } from '../../../../models/price/tariff-model';
import { CellObject, read } from 'xlsx';
import * as dayjs from 'dayjs';

@Component({
  selector: 'psc-calculator-consumption-input',
  templateUrl: './calculator-consumption-input.component.html',
  animations: [
    inOutAnimation({ enterDelay: 0.5 }),
  ],
})
export class CalculatorConsumptionInputComponent extends AbstractCalculatorComponentDirective implements OnInit {

  showMultipleConsumptionInputDialog: boolean = false;
  currency: string;

  protected readonly PriceCountry = PriceCountry;
  protected readonly PriceType = PriceType;
  protected readonly ConsumptionValueInputType = ConsumptionValueInputType;

  selectedTariff: TariffModel | undefined;

  consumptionOverHigherPriceLimit: boolean;
  higherPriceLimit: number = 6000;
  typeCalculationSelected: boolean;
  invalidConsumptionFile: boolean;
  readonly numberOfRowsForConsumptionData: number = 8760;

  get consumption(): FormArray {
    return this.formGroup.controls['consumption'] as FormArray;
  }

  get consumptionInputType() {
    return this.formGroup.get('consumption_input_type');
  }

  get consumptionUnitType() {
    return this.formGroup.controls['consumption_unit_type'];
  }

  get consumptionFile() {
    return this.formGroup.controls['consumption_file'];
  }

  ngOnInit(): void {
    if (!this.advanced) {
      this.formGroup.addControl('consumption_average_bill', new FormControl('', [Validators.required, Validators.min(0)]));
    }

    this.subs.add(
      this.consumptionUnitType?.valueChanges.subscribe((isInCurrency) => {
        this.consumptionUnitTypeChanged(isInCurrency);
      }),
      this.consumptionInputType?.valueChanges.subscribe((value) => {
        this.consumptionInputTypeChanged(value);
      }),
      this.manager.tariff_group.subscribe((value) => {
        this.tariffChanged(value);
      }),
      this.manager.type_calculation.subscribe((value) => {
        this.typeCalculationChanged(value);
      }),
    );

    this.handleConsumptionFileChange();
  }

  private consumptionUnitTypeChanged(isInCurrency: boolean) {
    if (isInCurrency) {
      this.consumptionInputType?.setValue(ConsumptionValueInputType.MonthlyBill);
    } else {
      this.consumptionInputType?.reset();
    }
    this.formGroup.updateValueAndValidity();
  }

  private tariffChanged(value) {
    if (!value) {
      this.selectedTariff = undefined;
      return;
    }
    this.selectedTariff = this.manager.tariffModelValues.find(model => model.priceId === value);
    if (!this.selectedTariff) {
      return;
    }
    this.manager.tariffHasTwoPrices = !!this.selectedTariff?.hasTwoPrices;
    this.manager.priceType = this.selectedTariff.type;
    this.currency = this.selectedTariff?.currency as string;
  }

  private typeCalculationChanged(value: ETypeCalculation) {
    this.typeCalculationSelected = !!value;
    if (!value) {
      return;
    }

    switch (value) {
      case ETypeCalculation.HR_FEED_IN_NET:
      case ETypeCalculation.BA_RS_FEED_IN_NET:
      case ETypeCalculation.BA_FE_FEED_IN_NET:
        if (this.consumptionUnitType.value) {
          this.consumptionInputType?.setValue(ConsumptionValueInputType.MonthlyBill);
        } else {
          this.consumptionInputType?.reset();
        }
        break;
      default:
        this.consumptionInputType?.setValue(ConsumptionValueInputType.Hourly);
    }
    this.formGroup.updateValueAndValidity();
  }

  saveMultipleConsumptions(values: number[]) {
    this.consumption.clear();
    for (const value of values) {
      this.consumption.push(new FormControl(value, [Validators.required, Validators.min(0)]));
    }
    this.formGroup.updateValueAndValidity();
    this.consumptionOverHigherPriceLimit = values.reduce((prev, curr) => prev + (+curr), 0) > this.higherPriceLimit;
    this.showMultipleConsumptionInputDialog = false;
  }

  handleConsumptionFileChange() {
    this.subs.sink = this.consumptionFile?.valueChanges.subscribe(async (files: File[]) => {
      if (!files || files.length === 0) {
        return;
      }
      if (this.consumptionFile.errors?.['fileType'] || this.consumptionFile.errors?.['fileSize']) {
        return;
      }
      const file = files[0];
      const fileExt = file.name.split('.').pop();
      if (fileExt === 'csv') {
        this.loadingService.showLoader();
        this.manager.consumptionFileData = [];
        this.csvParser.parseCsvFile(file, ';').subscribe({
          next: (result) => {
            this.manager.consumptionFileData.push(...ParseConsumption.parseConsumption(result.data));
            result.parser.resume();
          },
          complete: () => {
            this.loadingService.hideLoader();
          },
        });
        return;
      }

      // Excel file
      this.loadingService.showLoader();
      await this.parseDataFromExcel(file);
      this.loadingService.hideLoader();
    });
  }

  async parseDataFromExcel(file: File) {
    const data = await file.arrayBuffer();
    const workbook = read(data, { dense: true });

    const rows = workbook.Sheets[workbook.SheetNames[0]] as CellObject[][];

    if (rows.length < this.numberOfRowsForConsumptionData) {
      this.setConsumptionFileInvalid();
      return;
    }

    // Search for first date value to check where the data starts
    let startOfData = 0;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i]?.[0] && dayjs(rows[i][0].w).isValid()) {
        startOfData = i;
        break;
      }
    }

    if (startOfData === rows.length) {
      this.setConsumptionFileInvalid();
      return;
    }


    // This saves the hour that the sum will represent. e.g. 25.3.2023 20:15, 25.3.2023 20:30, 25.3.2023 20:45 and 25.3.2023 21:00 have their
    // consumptions summed up and set as consumption value for 25.3.2023 20:00
    let currentHourSumDate = dayjs(rows[startOfData][0].w).set('hour', 0).set('minute', 0);
    // Sum of the consumptions for the current hour that we sum for
    let sum: number = 0;
    let consumptions: number[] = [];

    const newTable: any[][] = [];
    for (let i = startOfData; i < rows.length; i++) {
      if (newTable.length === this.numberOfRowsForConsumptionData) {
        break;
      }
      const row = rows[i];

      const date = dayjs(row[0].w);
      if (!date.isValid() || !row[1]?.w || isNaN(+row[1].w)) {
        break;
      }
      // Skip February 29th if it's a leap year.
      if (date.date() === 29 && date.month() === 1) {
        continue;
      }
      const consumption = (+row[1].w);
      sum += consumption;
      consumptions.push(consumption);

      if (date.minute() == 0 && date.hour() - 2 === currentHourSumDate.hour()) {
        const missingDate = currentHourSumDate.add(1, 'hour');
        newTable.push([missingDate.format('DD.MM. HH:mm'), missingDate.year().toString(), 0]);
      }

      if (date.minute() == 0 && date.hour() !== currentHourSumDate.hour()) {
        newTable.push([
          currentHourSumDate.format('DD.MM. HH:mm'),
          currentHourSumDate.year().toString(), sum.toString().replace('.', ','),
          consumptions.length === 1 ? null : consumptions.map(c => (c * consumptions.length).toString().replace('.', ','))]);
        sum = 0;
        consumptions = [];
        currentHourSumDate = date;
      }
    }
    if (newTable.length !== this.numberOfRowsForConsumptionData) {
      this.setConsumptionFileInvalid();
      return;
    }

    this.manager.consumptionFileData = ParseConsumption.parseConsumption(newTable);
  }

  setConsumptionFileInvalid() {
    this.invalidConsumptionFile = true;
    this.consumptionFile.reset();
    this.formGroup.updateValueAndValidity();
    this.changeDetector.detectChanges();
  }

  private consumptionInputTypeChanged(value: ConsumptionValueInputType) {
    this.consumptionOverHigherPriceLimit = false;

    if (value === ConsumptionValueInputType.Hourly) {
      this.formGroup.controls['consumption_file'].addValidators(Validators.required);
    } else {
      this.formGroup.controls['consumption_file'].removeValidators(Validators.required);
    }

    if (value === ConsumptionValueInputType.MonthlyBill) {
      this.formGroup.addControl('consumption_average_bill', new FormControl('', [Validators.required, Validators.min(0)]));
    } else {
      this.formGroup.removeControl('consumption_average_bill');
    }

    if (value === ConsumptionValueInputType.MonthlyAverage) {
      this.consumption.push(new FormControl('', [Validators.required, Validators.min(0)]));
      if (this.manager.tariffHasTwoPrices) {
        this.consumption.push(new FormControl('', [Validators.required, Validators.min(0)]));
        this.subs.add(
          combineLatest([
            this.consumption.at(0).valueChanges,
            this.consumption.at(1).valueChanges,
          ]).subscribe(([first, second]) => {
            this.consumptionOverHigherPriceLimit = false;
            if (!isNaN(+first) && !isNaN(+second)) {
              this.consumptionOverHigherPriceLimit = (+first) + (+second) > this.higherPriceLimit / 12;
            }
          }),
        );
      } else {
        this.subs.sink = this.consumption.at(0).valueChanges.subscribe((value) => {
          this.consumptionOverHigherPriceLimit = false;
          if (!isNaN(+value)) {
            this.consumptionOverHigherPriceLimit = +value > this.higherPriceLimit / 12;
          }
        });
      }
    } else {
      this.consumption.clear();
    }
    this.formGroup.updateValueAndValidity();
    this.changeDetector.detectChanges();
  }
}
